"use client";

/**
 * LiquidSelectList — mismo diseño/API de superficie que LiquidPillDropdown.
 *
 * Únicas diferencias geométricas:
 * 1. Ancho del panel = ancho del trigger (no crece en X).
 * 2. Ancla left-0 + transform-origin top left (abre hacia abajo).
 *
 * Colores, backgrounds, brillos, reactive, shadow, appearance → mismos props
 * y mismo LiquidSurface / LiquidPill que el dropdown.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";
import {
  LiquidPill,
  type LiquidPillAppearance,
  type LiquidPillSize,
} from "./liquid-pill";
import {
  LiquidSurface,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
  type LiquidSurfaceShadow,
  type LiquidSurfaceStyle,
} from "./liquid-surface";
import { LiquidPillGroupVertical } from "./liquid-pill-group-vertical";
import { Z_INDEX } from "./z-index";

export interface LiquidSelectListItem {
  value: string;
  label: React.ReactNode;
  icon?: React.ElementType | React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
}

export interface LiquidSelectListProps {
  items?: LiquidSelectListItem[];
  data?: any[];
  labelKey?: string;
  valueKey?: string;
  value?: any;
  onSelect?: (value: string, item?: any) => void;
  /** @deprecated legacy rsuite-style alias */
  onChange?: (value: string, item?: any) => void;
  /** @deprecated accepted for legacy call sites */
  onSearch?: (value: string) => void;
  onHoverChange?: (value: string | null) => void;
  placeholder?: React.ReactNode;
  icon?: React.ElementType | React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
  active?: boolean;
  size?: LiquidPillSize;
  collapsedShape?: "circle" | "pill" | "rounded";
  showLabelWhenClosed?: boolean;
  /** LiquidPill / LiquidSurface: "normal" | "tinted" */
  appearance?: LiquidPillAppearance;
  /** LiquidSurface shadow del trigger */
  shadow?: LiquidSurfaceShadow;
  /** Partícula especular del trigger */
  cursorGlow?: boolean;
  /** Variante visual compuesta del LiquidSurface del trigger */
  surfaceStyle?: LiquidSurfaceStyle;
  itemClassName?: string;
  className?: string;
  style?: React.CSSProperties;
  block?: boolean;
  searchable?: boolean;
  cleanable?: boolean;
  loading?: boolean;
  menuMaxHeight?: number;
  renderMenu?: (menu: React.ReactNode) => React.ReactNode;
  renderMenuItem?: (label: React.ReactNode, item: any) => React.ReactNode;
  renderValue?: (value: any, item: any) => React.ReactNode;
  closeOnSelect?: boolean;
  shouldCloseOnSelect?: (value: string, item?: any) => boolean;
  listSize?: "xxs" | "xs" | "sm" | "md";
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  listDeformAxis?: LiquidDeformAxis;
  menuZIndex?: number;
  /** Activa el material reactivo al fondo en el trigger (LiquidSurface). */
  reactive?: boolean;
  "aria-label"?: string;
}

function renderIcon(icon: LiquidSelectListItem["icon"], size = 16) {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  return React.createElement(icon as React.ElementType, {
    size,
    strokeWidth: 1.75,
  });
}

const COLLAPSED_BOX: Record<
  Exclude<LiquidPillSize, "card-cta">,
  { size: number; iconSize: number }
> = {
  xxs: { size: 20, iconSize: 10 },
  xs: { size: 24, iconSize: 14 },
  sm: { size: 28, iconSize: 14 },
  md: { size: 36, iconSize: 16 },
  lg: { size: 44, iconSize: 18 },
  xl: { size: 56, iconSize: 20 },
  "2xl": { size: 64, iconSize: 22 },
};

const OPEN_MS = 520;
const CLOSE_MS = 280;
const EASE = "cubic-bezier(0.34, 1.2, 0.64, 1)";

type LabelLiftState = {
  label: React.ReactNode;
  top: number;
  left: number;
  width: number;
  settling: boolean;
};

export function LiquidSelectList({
  items: itemsProp,
  data,
  labelKey = "label",
  valueKey = "value",
  value,
  onSelect,
  onChange,
  onHoverChange,
  placeholder,
  icon,
  variant,
  disabled = false,
  active = false,
  size = "md",
  collapsedShape = "pill",
  showLabelWhenClosed = true,
  appearance = "normal",
  shadow = "default",
  cursorGlow = true,
  surfaceStyle,
  className,
  style,
  closeOnSelect = true,
  shouldCloseOnSelect,
  listSize = "xxs",
  bounceIntensity = "strong",
  deformAxis = "both",
  listDeformAxis = "vertical",
  menuZIndex = Z_INDEX.DROPDOWN,
  reactive = false,
  "aria-label": ariaLabel,
}: LiquidSelectListProps) {
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const triggerLabelRef = React.useRef<HTMLSpanElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const headerSlotRef = React.useRef<HTMLSpanElement>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const liftTimerRef = React.useRef<number | null>(null);
  const [mounted, setMounted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [panelExpanded, setPanelExpanded] = React.useState(false);
  const [rect, setRect] = React.useState({ top: 0, left: 0, width: 0 });
  /** Label visible en la cabecera del morph (no salta al seleccionar). */
  const [headerLabel, setHeaderLabel] = React.useState<React.ReactNode>(null);
  const [headerFading, setHeaderFading] = React.useState(false);
  /** Texto que sube desde la fila hasta la cabecera. */
  const [labelLift, setLabelLift] = React.useState<LabelLiftState | null>(null);

  const items: LiquidSelectListItem[] = React.useMemo(() => {
    if (itemsProp) return itemsProp;
    if (data) {
      return data.map((item) => ({
        value: String(item[valueKey]),
        label: React.isValidElement(item[labelKey])
          ? item[labelKey]
          : String(item[labelKey]),
        icon: item.icon,
        variant: item.variant,
        disabled: item.disabled,
      }));
    }
    return [];
  }, [itemsProp, data, labelKey, valueKey]);

  const selectedItem = items.find(
    (item) =>
      value != null &&
      value !== "" &&
      String(item.value) === String(value),
  );
  const displayVariant = resolveCircleActionVariant(
    selectedItem?.variant ?? variant ?? "slate",
  );
  // Botón cerrado: siempre el label del value actual.
  const triggerLabel = selectedItem?.label ?? placeholder;
  const triggerIcon = selectedItem?.icon ?? icon;
  // Cabecera del morph: se actualiza al terminar el lift (no al instante).
  const morphLabel = headerLabel ?? triggerLabel;
  const effectiveCollapsedShape =
    collapsedShape === "circle" && !triggerIcon ? "pill" : collapsedShape;
  const shouldShowClosedLabel =
    showLabelWhenClosed || effectiveCollapsedShape !== "circle";
  const box = COLLAPSED_BOX[size === "card-cta" ? "md" : size];
  const listIconSize = listSize === "xxs" ? 12 : listSize === "xs" ? 14 : 16;
  const listCheckClass = listSize === "xxs" ? "h-3 w-3" : "h-3.5 w-3.5";
  // Misma tipografía que LiquidPillGroupVertical (evita salto visual al subir el texto).
  const listTextClass =
    listSize === "xxs"
      ? "text-[9px]"
      : listSize === "xs"
        ? "text-[10px]"
        : listSize === "sm"
          ? "text-[11px]"
          : "text-[12px]";
  const labelTextClass = cn(
    listTextClass,
    "font-medium tracking-[-0.01em] truncate whitespace-nowrap text-left",
  );
  const labelMeasureClass = cn(
    listTextClass,
    "font-medium tracking-[-0.01em] whitespace-nowrap text-left",
  );
  const pillPadX =
    size === "xxs"
      ? "px-1.5"
      : size === "xs"
        ? "px-2"
        : size === "sm"
          ? "px-3"
          : size === "md"
            ? "px-3.5"
            : size === "lg"
              ? "px-4"
              : size === "xl"
                ? "px-5"
                : "px-6";
  const pillGap =
    size === "xxs" || size === "xs"
      ? "gap-1"
      : size === "sm"
        ? "gap-1.5"
        : size === "xl" || size === "2xl"
          ? "gap-2.5"
          : "gap-2";
  const rowContentClass = cn("flex w-full min-w-0 items-center", pillGap);

  /** Filas a medir: placeholder + cada ítem (icono + label + chevron + padding). */
  const measureRows = React.useMemo(() => {
    const rows: { key: string; label: React.ReactNode; icon?: LiquidSelectListItem["icon"] }[] =
      [];
    if (placeholder != null) {
      rows.push({ key: "__placeholder", label: placeholder, icon });
    }
    items.forEach((item, index) => {
      if (item.label == null) return;
      rows.push({
        key: `item-${item.value}-${index}`,
        label: item.label,
        icon: item.icon ?? icon,
      });
    });
    return rows;
  }, [items, placeholder, icon]);

  const measureRef = React.useRef<HTMLSpanElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    if (
      !measureRef.current ||
      typeof window === "undefined" ||
      effectiveCollapsedShape === "circle" ||
      !shouldShowClosedLabel
    ) {
      setTriggerWidth(null);
      return;
    }
    const nodes = measureRef.current.querySelectorAll<HTMLElement>(
      "[data-lsl-measure-row]",
    );
    let max = 0;
    nodes.forEach((node) => {
      // scrollWidth evita 0 por colapso de % width / truncate en el árbol visible.
      max = Math.max(
        max,
        Math.ceil(node.scrollWidth),
        Math.ceil(node.getBoundingClientRect().width),
      );
    });
    setTriggerWidth(max > 0 ? max : null);
  }, [measureRows, size, listSize, effectiveCollapsedShape, shouldShowClosedLabel]);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const captureRect = React.useCallback(() => {
    if (!triggerRef.current || typeof window === "undefined") return;
    const r = triggerRef.current.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width });
  }, []);

  React.useEffect(() => {
    if (!open || typeof window === "undefined") return;
    captureRect();
    const update = () => captureRect();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    let ro: ResizeObserver | undefined;
    if (triggerRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(triggerRef.current);
    }
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [open, captureRect]);

  React.useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      setPanelExpanded(true);
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const clearLiftTimer = () => {
    if (liftTimerRef.current != null) {
      window.clearTimeout(liftTimerRef.current);
      liftTimerRef.current = null;
    }
  };

  const openPanel = () => {
    if (disabled) return;
    clearCloseTimer();
    clearLiftTimer();
    captureRect();
    setLabelLift(null);
    setHeaderFading(false);
    setHeaderLabel(selectedItem?.label ?? placeholder ?? null);
    setOpen(true);
    setPanelExpanded(false);
  };

  const closePanel = React.useCallback(() => {
    clearCloseTimer();
    clearLiftTimer();
    setLabelLift(null);
    setHeaderFading(false);
    setPanelExpanded(false);
    onHoverChange?.(null);
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_MS);
  }, [onHoverChange]);

  React.useEffect(
    () => () => {
      clearCloseTimer();
      clearLiftTimer();
    },
    [],
  );

  const handleTriggerClick = () => {
    if (open) closePanel();
    else openPanel();
  };

  const handleSelect = (item: LiquidSelectListItem) => {
    if (item.disabled) return;

    onSelect?.(item.value, item);
    onChange?.(item.value, item);
    onHoverChange?.(null);

    const shouldClose = shouldCloseOnSelect
      ? shouldCloseOnSelect(item.value, item)
      : closeOnSelect;

    // Subida fluida del texto de la fila → cabecera (evita el teletransporte).
    if (shouldShowClosedLabel && shouldClose && typeof window !== "undefined") {
      const headerEl = headerSlotRef.current;
      const listRoot = panelRef.current?.querySelector(
        ".liquid-select-list-list",
      );
      const rows = listRoot?.querySelectorAll("button");
      const idx = items.findIndex(
        (i) => String(i.value) === String(item.value),
      );
      const row = idx >= 0 ? rows?.[idx] : null;

      if (headerEl && row) {
        const fromEl =
          (row.querySelector("span.truncate") as HTMLElement | null) ?? row;
        const from = fromEl.getBoundingClientRect();

        clearLiftTimer();
        clearCloseTimer();
        setHeaderFading(true);
        setLabelLift({
          label: item.label,
          top: from.top,
          left: from.left,
          width: from.width,
          settling: false,
        });
        // Cierre + subida en paralelo.
        setPanelExpanded(false);

        // Remedir destino tras el commit del value (label nuevo en el trigger oculto).
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const toEl = triggerLabelRef.current ?? headerEl;
            const to = toEl.getBoundingClientRect();
            setLabelLift({
              label: item.label,
              top: to.top,
              left: to.left,
              width: to.width,
              settling: true,
            });
          });
        });

        // Handoff sin frame vacío: trigger visible debajo → snap exacto → quitar vuelo.
        liftTimerRef.current = window.setTimeout(() => {
          setHeaderLabel(item.label);
          setHeaderFading(false);
          setOpen(false);
          liftTimerRef.current = null;
          requestAnimationFrame(() => {
            const finalEl = triggerLabelRef.current;
            if (finalEl) {
              const final = finalEl.getBoundingClientRect();
              setLabelLift({
                label: item.label,
                top: final.top,
                left: final.left,
                width: final.width,
                settling: false,
              });
            }
            requestAnimationFrame(() => {
              setLabelLift(null);
            });
          });
        }, CLOSE_MS);
        return;
      }
    }

    if (shouldClose) {
      setHeaderLabel(item.label);
      closePanel();
    } else {
      setHeaderLabel(item.label);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node) {
        if (triggerRef.current?.contains(target)) return;
        if (panelRef.current?.contains(target)) return;
      }
      closePanel();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePanel();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closePanel]);

  // Ancho del panel = ancho real del trigger (pill o circle).
  const panelWidth =
    rect.width > 0
      ? rect.width
      : effectiveCollapsedShape === "circle"
        ? box.size
        : box.size;

  // Con ancho fijo medido: label puede flex + truncate.
  // Sin ancho fijo: NUNCA flex-1/min-w-0 — colapsa a solo chevron (parece círculo).
  const hasFixedTriggerWidth = triggerWidth != null;
  const triggerContent = (
    <>
      {renderIcon(triggerIcon, box.iconSize)}
      {shouldShowClosedLabel && triggerLabel != null && (
        <span
          ref={triggerLabelRef}
          className={cn(
            labelTextClass,
            hasFixedTriggerWidth ? "min-w-0 flex-1" : "shrink-0",
          )}
        >
          {triggerLabel}
        </span>
      )}
      {effectiveCollapsedShape !== "circle" && (
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      )}
    </>
  );

  return (
    <>
      {/* Medidor oculto off-screen: ancho = max(placeholder, ítems). */}
      {shouldShowClosedLabel && effectiveCollapsedShape !== "circle" && (
        <span
          ref={measureRef}
          aria-hidden
          className="pointer-events-none fixed whitespace-nowrap"
          style={{ left: -9999, top: 0, visibility: "hidden" }}
        >
          {measureRows.map((row) => (
            <span
              key={row.key}
              data-lsl-measure-row
              className={cn("flex items-center", pillPadX, pillGap)}
            >
              {renderIcon(row.icon, box.iconSize)}
              <span className={labelMeasureClass}>{row.label}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            </span>
          ))}
        </span>
      )}
      <span
        ref={triggerRef}
        className={cn(
          "relative inline-block align-top",
          open && "pointer-events-none opacity-0",
          "liquid-select-list-trigger",
          className,
        )}
        style={
          effectiveCollapsedShape === "circle"
            ? { width: `${box.size}px`, height: `${box.size}px`, ...style }
            : {
                minHeight: `${box.size}px`,
                ...(hasFixedTriggerWidth ? { width: `${triggerWidth}px` } : null),
                ...style,
              }
        }
      >
        <LiquidPill
          as="button"
          type="button"
          variant={displayVariant}
          appearance={appearance}
          shadow={shadow}
          cursorGlow={cursorGlow}
          surfaceStyle={surfaceStyle}
          shape={
            effectiveCollapsedShape === "rounded"
              ? "rounded"
              : effectiveCollapsedShape === "pill"
                ? "pill"
                : "circle"
          }
          size={size}
          active={active}
          disabled={disabled}
          hoverDeform={!open}
          hoverBounce={!open}
          bounceIntensity={bounceIntensity}
          deformAxis={deformAxis}
          reactive={reactive}
          onClick={handleTriggerClick}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn(
            hasFixedTriggerWidth && "w-full justify-between",
          )}
        >
          <span
            className={cn(
              "relative z-[3]",
              hasFixedTriggerWidth ? rowContentClass : cn("inline-flex items-center", pillGap),
            )}
          >
            {triggerContent}
          </span>
        </LiquidPill>
      </span>

      {mounted &&
        open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: `${panelWidth}px`,
              height: `${box.size}px`,
              zIndex: menuZIndex,
            }}
          >
            <div
              className={cn(
                "liquid-select-list absolute left-0 top-0 overflow-hidden",
                panelExpanded && "liquid-select-list--is-open",
              )}
              style={{
                transitionProperty:
                  "min-width, max-width, width, min-height, max-height, padding",
                transitionDuration: `${panelExpanded ? OPEN_MS : CLOSE_MS}ms`,
                transitionTimingFunction: EASE,
                width: `${panelWidth}px`,
                minWidth: `${panelWidth}px`,
                maxWidth: `${panelWidth}px`,
                minHeight: panelExpanded ? `${box.size + 8}px` : `${box.size}px`,
                maxHeight: panelExpanded ? "460px" : `${box.size}px`,
                padding: panelExpanded ? "8px 8px" : "0px",
                borderRadius: "min(50%, 20px)",
              }}
            >
              <LiquidSurface
                as="div"
                variant={displayVariant}
                tone="tinted"
                shape="pill"
                size="free"
                interactive={false}
                cursorGlow={false}
                hoverDeform={false}
                hoverBounce={false}
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                }}
              />
              <span
                aria-hidden
                className="liquid-select-list-white-surface pointer-events-none absolute inset-0"
                style={{
                  opacity: panelExpanded ? 1 : 0,
                  transition: `opacity ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}`,
                  borderRadius: "inherit",
                }}
              />
              <span className="relative z-[3] flex w-full min-w-0 flex-col items-stretch">
                {/* Cabecera: el label anterior se desvanece; el nuevo sube con labelLift. */}
                {shouldShowClosedLabel && (
                  <span
                    className={cn(
                      "pointer-events-none flex w-full shrink-0 items-center",
                      pillPadX,
                      pillGap,
                    )}
                    style={{ height: `${box.size}px` }}
                  >
                    {renderIcon(triggerIcon, box.iconSize)}
                    {morphLabel != null && (
                      <span
                        ref={headerSlotRef}
                        className={cn("min-w-0 flex-1", labelTextClass)}
                        style={{
                          opacity: headerFading || labelLift ? 0 : 1,
                          transition: `opacity ${Math.min(CLOSE_MS, 200)}ms ${EASE}`,
                        }}
                      >
                        {morphLabel}
                      </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  </span>
                )}
                <span
                  aria-hidden={!panelExpanded}
                  className="liquid-select-list-list flex min-w-0 flex-col overflow-hidden"
                  style={{
                    transition: `max-height ${panelExpanded ? OPEN_MS : CLOSE_MS}ms ${EASE}, opacity ${Math.min(panelExpanded ? OPEN_MS : CLOSE_MS, 280)}ms ${EASE}`,
                    maxHeight: panelExpanded ? "380px" : "0px",
                    opacity: panelExpanded ? 1 : 0,
                    pointerEvents: panelExpanded ? "auto" : "none",
                  }}
                >
                  <LiquidPillGroupVertical
                    items={items.map((item) => ({
                      value: item.value,
                      label: item.label,
                      icon: renderIcon(item.icon, listIconSize),
                      variant: item.variant,
                      disabled: item.disabled,
                      trailing:
                        value != null &&
                        value !== "" &&
                        String(item.value) === String(value) ? (
                          <Check
                            className={cn("shrink-0", listCheckClass)}
                            strokeWidth={2.5}
                          />
                        ) : null,
                    }))}
                    activeValue={
                      value != null && value !== "" ? String(value) : null
                    }
                    onChange={(v) => {
                      const item = items.find(
                        (i) => String(i.value) === String(v),
                      );
                      if (item) handleSelect(item);
                    }}
                    onHover={onHoverChange}
                    size={listSize}
                    bounceIntensity={bounceIntensity}
                    deformAxis={listDeformAxis}
                  />
                </span>
              </span>
            </div>
          </div>,
          document.body,
        )}

      {/* Texto en vuelo fuera del portal del panel: sobrevive al unmount y cubre el handoff. */}
      {mounted &&
        labelLift &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            aria-hidden
            className={cn(
              "pointer-events-none fixed text-slate-700 dark:text-zinc-200",
              labelTextClass,
            )}
            style={{
              top: labelLift.top,
              left: labelLift.left,
              width: labelLift.width,
              zIndex: menuZIndex + 1,
              transition: labelLift.settling
                ? `top ${CLOSE_MS}ms ${EASE}, left ${CLOSE_MS}ms ${EASE}, width ${CLOSE_MS}ms ${EASE}`
                : "none",
            }}
          >
            {labelLift.label}
          </span>,
          document.body,
        )}

      <style jsx global>{`
        /* Idéntico a .liquid-pill-dropdown (solo renombre + transform-origin). */
        .liquid-select-list > .liquid-surface {
          background: rgba(255, 255, 255, 0.72) !important;
          backdrop-filter: blur(24px) saturate(1.4);
          -webkit-backdrop-filter: blur(24px) saturate(1.4);
        }
        .dark .liquid-select-list > .liquid-surface {
          background: rgba(16, 16, 20, 0.88) !important;
          backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
          -webkit-backdrop-filter: blur(24px) saturate(1.02) brightness(0.68);
        }
        .liquid-select-list > .liquid-surface::before,
        .liquid-select-list > .liquid-surface::after,
        .liquid-select-list > .liquid-surface > .liquid-surface__hover-overlay,
        .liquid-select-list > .liquid-surface > .liquid-surface__cursor-glow {
          display: none !important;
        }
        .liquid-select-list-white-surface {
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(20px) saturate(1.3);
          -webkit-backdrop-filter: blur(20px) saturate(1.3);
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
        }
        .dark .liquid-select-list-white-surface {
          background: rgba(16, 16, 20, 0.84);
          backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          -webkit-backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
        }
        .liquid-select-list {
          transition-property:
            min-width, max-width, width, min-height, max-height, padding, box-shadow;
          transform-origin: top left;
        }
        .liquid-select-list--is-open {
          box-shadow:
            0 1px 2px rgba(15,23,42,0.04),
            0 12px 28px -10px rgba(15,23,42,0.22),
            0 24px 48px -20px rgba(15,23,42,0.18) !important;
        }
        .dark .liquid-select-list--is-open {
          box-shadow:
            0 1px 2px rgba(0,0,0,0.4),
            0 12px 28px -10px rgba(0,0,0,0.55),
            0 24px 48px -20px rgba(0,0,0,0.45) !important;
        }
        .liquid-select-list--is-open > .liquid-surface::before,
        .liquid-select-list--is-open > .liquid-surface::after {
          display: none;
        }
      `}</style>
    </>
  );
}
